/**
 * modulesService.ts
 * Service to fetch and manage framework modules from Firestore
 */

import { db } from '@/lib/firebase';
import { collection, getDocs } from 'firebase/firestore';

export interface ModuleFramework {
    id: string;
    displayName: string;
    slug: string;
    aliases: string[];
    patterns: string[];
    tags?: string[];
    plans: string[];
    logo?: string;
    mcmReference?: string;
    levels?: string[];
    levelCategories?: string[];
    additionalCost?: number;
    features?: string[];
    framework: {
        name: string;
    };
    version: {
        clauses: Array<string | { __ref__?: string; __ref?: string }>;
        controls: Array<string | { __ref__?: string; __ref?: string }>;
        tasks: Array<string | { __ref__?: string; __ref?: string }>;
        version: string;
        designators?: string[];
        rootKey?: string;
        components?: Array<{ __ref__?: string; __ref?: string }>;
    };
}

export interface FrameworkOption {
    id: string;
    label: string;
    slug: string;
    dbKey: string; // The key used in enabledModules check
}

/**
 * Fetches all frameworks from the modules collection
 */
export async function getAllFrameworks(): Promise<ModuleFramework[]> {
    try {
        const modulesRef = collection(db, 'modules');
        const snapshot = await getDocs(modulesRef);
        
        const frameworks: ModuleFramework[] = [];
        
        snapshot.forEach((doc) => {
            const data = doc.data();
            // console.log(data?.version || 'no version');
            frameworks.push({
                id: doc.id,
                displayName: data.displayName || doc.id,
                slug: data.slug || doc.id.toLowerCase(),
                aliases: data.aliases || [],
                patterns: data.patterns || [],
                tags: data.tags || [],
                plans: data.plans || [],
                logo: data.logo,
                mcmReference: data.mcmReference,
                levels: data.levels,
                levelCategories: data.levelCategories,
                additionalCost: data.additionalCost,
                features: data.features,
                framework: data.framework || { name: doc.id },
                version: data.version || {
                    clauses: [],
                    controls: [],
                    tasks: [],
                    version: 'unknown'
                }
            });
        });
        
        return frameworks;
    } catch (error) {
        console.error('[modulesService] Error fetching frameworks:', error);
        return [];
    }
}

/**
 * Converts a framework to a control ID format used in SOA
 * This maps framework IDs to their control collection identifiers
 */
export function getFrameworkControlId(framework: ModuleFramework): string {
    const id = framework.id.toLowerCase();
    
    // Map framework IDs to their control identifiers
    const mappings: Record<string, string> = {
        'disp': 'dispcontrols',
        'essentialeight': 'e8controls',
        'iso27001': 'iso270012022annexacontrols',
        'iso27701': 'iso27701',
        'iso42001': 'iso420012023controls',
        'iso14001': 'iso14001',
        'iso45001': 'iso45001',
        'iso9001': 'iso9001',
        'is18': 'qgov-is18',
        'ism': 'ism',
        'privacyact': 'pacontrols',
        'soc2': 'soc2controls',
        'smb1001': 'smb1001controls',
    };
    
    return mappings[id] || id.toLowerCase().replace(/[\s\-]/g, '') + 'controls';
}

/**
 * Gets the database key for checking if a module is enabled
 * This maps framework IDs to the keys used in complianceModules settings
 */
export function getFrameworkDbKey(framework: ModuleFramework): string {
    const id = framework.id;
    
    // Map framework IDs to their database keys in complianceModules
    const mappings: Record<string, string> = {
        'DISP': 'disp',
        'EssentialEight': 'essentialeight',
        'ISO27001': 'iso27001-2013', // ISO 27001 uses this key
        'ISO27701': 'iso27701',
        'ISO42001': 'iso42001',
        'ISO14001': 'iso14001',
        'ISO45001': 'iso45001',
        'ISO9001': 'iso9001',
        'IS18': 'is18',
        'ISM': 'ism',
        'PrivacyAct': 'privacyact',
        'SOC2': 'soc2',
        'SMB1001': 'smb1001',
    };
    
    return mappings[id] || id.toLowerCase().replace(/[\s\-\/]/g, '');
}

/**
 * Converts frameworks to framework options for the UI
 */
export function frameworksToOptions(frameworks: ModuleFramework[]): FrameworkOption[] {
    return frameworks.map(fw => ({
        id: getFrameworkControlId(fw),
        label: fw.displayName,
        slug: fw.slug,
        dbKey: getFrameworkDbKey(fw)
    }));
}

/**
 * Filters frameworks to only those that are enabled for the customer
 * @param frameworks - All available frameworks
 * @param enabledModules - Array of enabled module keys (lowercase)
 * @param isModuleEnabled - Function to check if a module is enabled
 */
export function filterEnabledFrameworks(
    frameworks: FrameworkOption[],
    enabledModules: string[],
    isModuleEnabled?: (moduleName: string, dbName?: string) => boolean
): FrameworkOption[] {
    if (!enabledModules || enabledModules.length === 0) {
        return [];
    }
    
    const normalizedEnabled = enabledModules.map(m => m.toLowerCase());
    
    return frameworks.filter(fw => {
        const dbKey = fw.dbKey.toLowerCase();
        
        // Check using isModuleEnabled function if provided
        if (isModuleEnabled) {
            if (isModuleEnabled(fw.label, dbKey)) {
                return true;
            }
        }
        
        // Direct match
        if (normalizedEnabled.includes(dbKey)) {
            return true;
        }
        
        // Check for partial matches (e.g., 'iso27001' should match 'iso27001-2013')
        for (const enabled of normalizedEnabled) {
            if (enabled.includes(dbKey) || dbKey.includes(enabled)) {
                return true;
            }
            // Handle variations like 'iso 27001' vs 'iso27001'
            const normalizedDbKey = dbKey.replace(/[\s\-\/]/g, '');
            const normalizedEnabled2 = enabled.replace(/[\s\-\/]/g, '');
            if (normalizedDbKey === normalizedEnabled2 || 
                normalizedDbKey.includes(normalizedEnabled2) || 
                normalizedEnabled2.includes(normalizedDbKey)) {
                return true;
            }
        }
        
        return false;
    });
}
